import { useCallback, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import classnames from 'classnames';
import { addMonths, isAfter, isBefore, isEqual } from 'date-fns';

import { DateRange } from '../../types/date-range.type';
import {
    getListOfMonthDays,
    MONTH_NAMES,
    WEEK_DAYS_INITIALS,
} from '../../utils/date.util';

import styles from './download-file-button.module.scss';

type Props = {
    range: DateRange;
    onDateSelect: (date: Date) => void;
    onDownload: () => void;
};

const DatePicker = ({ range, onDateSelect, onDownload }: Props) => {
    const [date, setDate] = useState(new Date());
    const days = useMemo(() => {
        return getListOfMonthDays(date, { startPadding: true });
    }, [date]);

    const changeDate = useCallback(
        (action: 'prev' | 'next') => {
            setDate(addMonths(date, action === 'prev' ? -1 : 1));
        },
        [date]
    );

    const isWithinRange = useCallback(
        (date: Date): boolean => {
            if (!range.from || !range.to) {
                return false;
            }
            return !isBefore(date, range.from) && !isAfter(date, range.to);
        },
        [range]
    );

    const isFrom = useCallback(
        (date: Date) => {
            if (range.from) return isEqual(date, range.from);
        },
        [range]
    );

    const isTo = useCallback(
        (date: Date) => {
            if (range.to) return isEqual(date, range.to);
        },
        [range]
    );

    const isButtonEnabled = useCallback(
        () => !!range.from && !!range.to,
        [range]
    );

    return (
        <div className={styles.datePicker}>
            <header>
                <div className={styles.monthHeader}>
                    <ChevronLeftIcon
                        size={20}
                        onClick={() => changeDate('prev')}
                    />
                    <span>
                        {MONTH_NAMES[date.getMonth()]} {date.getFullYear()}
                    </span>
                    <ChevronRightIcon
                        size={20}
                        onClick={() => changeDate('next')}
                    />
                </div>
                <div className={styles.weekHeader}>
                    {WEEK_DAYS_INITIALS.map((wd) => (
                        <span>{wd}</span>
                    ))}
                </div>
            </header>
            <div className={styles.dateList}>
                {days.map((d) => (
                    <div
                        className={classnames({
                            [styles.day]: true,
                            [styles.inRange]: d && isWithinRange(d),
                            [styles.isFrom]: d && isFrom(d),
                            [styles.isTo]: d && isTo(d),
                        })}
                        onClick={() => d && onDateSelect(d)}
                    >
                        {d?.getDate()}
                    </div>
                ))}
            </div>
            <footer>
                <button disabled={!isButtonEnabled()} onClick={onDownload}>
                    Descargar
                </button>
            </footer>
        </div>
    );
};

export default DatePicker;
